import {
  computePosition,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
} from "@floating-ui/dom";

function buildTooltipElement(theme) {
  const box = document.createElement("div");
  box.className = "app-tooltip";
  if (theme) box.dataset.theme = theme;

  const content = document.createElement("div");
  content.className = "app-tooltip__content";
  box.appendChild(content);

  const arrowEl = document.createElement("div");
  arrowEl.className = "app-tooltip__arrow";
  box.appendChild(arrowEl);

  return { box, content, arrowEl };
}

function setContent(contentEl, value, allowHTML) {
  if (allowHTML) {
    contentEl.innerHTML = value;
  } else {
    contentEl.textContent = value;
  }
}

export function initTooltip(reference, options = {}) {
  const {
    content = reference.dataset.tooltipContent || reference.getAttribute("title") || "",
    placement = reference.dataset.tooltipPlacement || "top",
    theme = null,
    allowHTML = false,
  } = options;

  const { box, content: contentEl, arrowEl } = buildTooltipElement(theme);
  setContent(contentEl, content, allowHTML);

  let cleanupAutoUpdate = null;

  function update() {
    computePosition(reference, box, {
      placement,
      middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
    }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
      box.dataset.placement = finalPlacement;
      Object.assign(box.style, { left: `${x}px`, top: `${y}px` });

      const side = finalPlacement.split("-")[0];
      const oppositeSide = { top: "bottom", right: "left", bottom: "top", left: "right" }[side];
      const arrowData = middlewareData.arrow;
      if (arrowData) {
        Object.assign(arrowEl.style, {
          left: arrowData.x != null ? `${arrowData.x}px` : "",
          top: arrowData.y != null ? `${arrowData.y}px` : "",
          [oppositeSide]: "-4px",
        });
      }
    });
  }

  function show() {
    if (box.isConnected) return;
    document.body.appendChild(box);
    box.style.position = "absolute";
    box.style.left = "0px";
    box.style.top = "0px";
    cleanupAutoUpdate = autoUpdate(reference, box, update);
  }

  function hide() {
    if (!box.isConnected) return;
    if (cleanupAutoUpdate) cleanupAutoUpdate();
    cleanupAutoUpdate = null;
    box.remove();
  }

  reference.addEventListener("mouseenter", show);
  reference.addEventListener("mouseleave", hide);
  reference.addEventListener("focus", show);
  reference.addEventListener("blur", hide);

  const instance = { reference, box, show, hide, setContent: (value) => setContent(contentEl, value, allowHTML) };
  reference._tooltip = instance;
  return instance;
}

export function initTooltips(selector, options = {}) {
  document.querySelectorAll(selector).forEach((el) => initTooltip(el, options));
}

export function destroyTooltip(reference) {
  const instance = reference._tooltip;
  if (!instance) return;
  instance.hide();
  reference.removeEventListener("mouseenter", instance.show);
  reference.removeEventListener("mouseleave", instance.hide);
  reference.removeEventListener("focus", instance.show);
  reference.removeEventListener("blur", instance.hide);
  delete reference._tooltip;
}
